import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Plus, Edit, Trash2, Eye, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageQuestionnairesPage = () => {
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/questionnaires`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionnaires(response.data.questionnaires || []);
    } catch (error) {
      toast.error('Failed to fetch questionnaires');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this questionnaire?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessments/questionnaires/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Questionnaire deleted successfully');
      fetchQuestionnaires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={colors[status] || colors.draft}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Questionnaires</h1>
            <p className="text-gray-600 mt-1">View, edit, and publish your assessments</p>
          </div>
          <Button onClick={() => navigate('/assessments/create-questionnaire')} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Create New
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : questionnaires.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">No questionnaires yet. Create your first assessment!</p>
            <Button onClick={() => navigate('/assessments/create-questionnaire')} className="bg-green-600">
              Create Questionnaire
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {questionnaires.map((q) => (
              <Card key={q.questionnaire_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-[18px] font-bold text-gray-900">{q.title}</h3>
                      {getStatusBadge(q.status)}
                      {q.purpose === 'adaptive' && (
                        <Badge className="bg-purple-100 text-purple-800">ADAPTIVE</Badge>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4">{q.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">{q.total_questions}</span>
                        <span>Questions</span>
                      </div>
                      {q.purpose === 'adaptive' && q.sequences && (
                        <div className="flex items-center space-x-1">
                          <span className="font-medium">{q.sequences.length}</span>
                          <span>Sequences</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">{q.total_points}</span>
                        <span>Total Points</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">{q.pass_score_percentage}%</span>
                        <span>Pass Score</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">{q.max_attempts}</span>
                        <span>Max Attempts</span>
                      </div>
                      {q.time_limit_minutes && (
                        <div className="flex items-center space-x-1">
                          <span className="font-medium">{q.time_limit_minutes}</span>
                          <span>Minutes</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/assessments/questionnaire/${q.questionnaire_id}`)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(q.questionnaire_id)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManageQuestionnairesPage;